// src/pages/SymptomsDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Input, Table, message, Space, Typography, Modal, Rate, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { diaryAPI } from '../utils/api';
import dayjs from 'dayjs';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const SymptomsDiary: React.FC = () => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getSymptomEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading symptom entries:', error);
      message.error('Ошибка загрузки записей симптомов');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      symptom_text: record.symptom_text,
      intensity: record.intensity,
      timestamp: dayjs(record.timestamp || record.entry_date),
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingId(null);
    form.setFieldsValue({
      intensity: 3,
      timestamp: dayjs(),
    });
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      const timestamp = values.timestamp
        ? values.timestamp.toISOString()
        : new Date().toISOString();
      const data = {
        symptom_text: values.symptom_text,
        intensity: values.intensity,
        timestamp,
        entry_date: dayjs(timestamp).format('YYYY-MM-DD'),
      };

      if (editingId) {
        await diaryAPI.updateSymptomEntry(editingId, data);
        message.success('Симптом успешно обновлен!');
      } else {
        await diaryAPI.createSymptomEntry(data);
        message.success('Симптом успешно добавлен!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving symptom entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения симптома');
    }
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Симптом',
      dataIndex: 'symptom_text',
      key: 'symptom_text',
    },
    {
      title: 'Интенсивность',
      dataIndex: 'intensity',
      key: 'intensity',
      render: (value: number, record: any) => {
        if (record.symptom_text === 'Нет симптомов') {
          return '-';
        }
        return '⭐'.repeat(value);
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Дневник симптомов</Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: isMobile ? 16 : 24 }}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342', width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Добавить симптом
          </Button>
          <Button
            onClick={async () => {
              try {
                const data = {
                  symptom_text: 'Нет симптомов',
                  intensity: 1,
                  timestamp: new Date().toISOString(),
                  entry_date: dayjs().format('YYYY-MM-DD'),
                };
                await diaryAPI.createSymptomEntry(data);
                message.success('Отмечено, что симптомов нет');
                await loadEntries();
              } catch (error: any) {
                console.error('Error saving no symptoms entry:', error);
                message.error(error.response?.data?.detail || 'Ошибка сохранения');
              }
            }}
            style={{ width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Симптомов нет
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={entries}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: isMobile ? 10 : 20, simple: isMobile }}
            scroll={isMobile ? { x: 'max-content' } : undefined}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>
      </Card>

      <Modal
        title={editingId ? 'Редактировать симптом' : 'Добавить симптом'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Описание симптома"
            name="symptom_text"
            rules={[{ required: true, message: 'Опишите симптом!' }]}
          >
            <Input.TextArea rows={4} placeholder="Опишите симптом..." />
          </Form.Item>

          <Form.Item
            label="Интенсивность (1-5)"
            name="intensity"
            rules={[{ required: true, message: 'Укажите интенсивность!' }]}
            initialValue={3}
          >
            <Rate count={5} />
          </Form.Item>

          <Form.Item
            label="Дата и время"
            name="timestamp"
            rules={[{ required: true, message: 'Укажите дату и время!' }]}
            initialValue={dayjs()}
          >
            <DatePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingId ? 'Обновить' : 'Сохранить'}
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
